<template>
  <div>
    <v-list
      class="py-0"
      dense
    >
      <v-list-item
        v-for="field in layer.details.filter(f => (!linkField || linkField.key !== f) && (!attachmentField || attachmentField.key !== f))"
        :key="field"
        class="px-0"
        style="min-height:24px;max-width:100%"
        two-line
      >
        <v-list-item-content class="py-1">
          <v-list-item-title>
            {{ fields[field].title || fields[field]['x-originalName'] || fields[field].key }}
          </v-list-item-title>
          <v-list-item-subtitle
            v-if="data"
            style="white-space: normal;"
          >
            <template v-if="!fields[field].separator">
              {{ formatField(data, fields[field])+'' }}
            </template>
            <template
              v-for="value in data[field].split(fields[field].separator).sort().slice(0, 3)"
              v-else-if="data[field]"
            >
              <v-chip
                :key="value"
                small
                style="flex:none"
                class="my-1 mr-1"
              >
                {{ (formatField({ [field]: value }, fields[field]) + '') }}
              </v-chip>
            </template>
          </v-list-item-subtitle>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="data && linkField && data[linkField.key]"
        class="px-0"
      >
        <v-list-item-content class="py-1">
          <v-list-item-title>
            <a
              :href="(!data[linkField.key].includes('http') ? 'http://' : '') + data[linkField.key]"
              target="_blank"
            >Page associée</a>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="data && attachmentField && data[attachmentField.key]"
        class="px-0"
      >
        <v-list-item-content class="py-1">
          <v-list-item-title>
            <a
              :href="(!data[attachmentField.key].includes('http') ? (dataset.href + '/attachments/') : '') + data[attachmentField.key]"
              target="_blank"
            >Document attaché</a>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="data && layer.linkTemplate && urlTemplate"
        class="px-0"
      >
        <v-list-item-content class="py-1">
          <v-list-item-title>
            <a
              :href="urlTemplate.expand(data) "
              target="_blank"
            >Lien associé</a>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
      <v-list-item
        v-if="data && layer.gpxDownload && data['_geoshape.type'].includes('String')"
        class="px-0"
      >
        <v-list-item-content class="py-1">
          <v-list-item-title>
            <a @click="downloadGPX(data)">Fichier GPX</a>
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>
    </v-list>
    <template v-if="data && layer.embed && layer.embed.type === 'embed'">
      <v-iframe
        v-if="layer.embed.element.type === 'tablePreview'"
        :src="`/data-fair/embed/dataset/${accessKey ? (accessKey + '%3A') : ''}${layer.embed.element.dataset.id}/table?display=${layer.embed.element.display}&interaction=${!layer.embed.element.noInteractions}${layer.embed.element.fields && layer.embed.element.fields.length ? ('&cols=' + layer.embed.element.fields.join(',')) : ''}`"
        :query-params-extra="queryParamsExtra"
        scrolling="yes"
        :style="`height:${tableHeight}px`"
      />
      <v-iframe
        v-else-if="layer.embed.element.type === 'application'"
        :src="`/data-fair/app/${accessKey ? (accessKey+'%3A') : ''}${layer.embed.element.application.id}`"
        :query-params-extra="queryParamsExtra"
        :style="layer.embed.element.application.baseApp.meta['df:overflow'] !== 'true' ? 'height:100%' : ''"
      />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import axios from 'axios'
import { formatField } from '@data-fair/components-app/format/fields'
import VIframe from '@koumoul/v-iframe'
import { parseTemplate } from 'url-template'
import GeoJsonToGpx from '@dwayneparton/geojson-to-gpx'

export default {
  components: { VIframe },
  props: {
    feature: { type: Object, required: true }
  },
  data: () => ({
    data: null
  }),
  computed: {
    ...mapState(['datasets']),
    ...mapGetters(['config', 'datasetInfos', 'legendIdx', 'accessKey']),
    idx () {
      if (!this.feature.source) return
      return Number(this.feature.source.split('-').pop())
    },
    layer () {
      return this.config.layers[this.idx]
    },
    fields () {
      return this.datasets[this.idx].schema.reduce((a, b) => { a[b.key] = b; return a }, {})
    },
    attachmentField () {
      return this.datasets[this.idx].schema.find(f => f['x-refersTo'] === 'http://schema.org/DigitalDocument')
    },
    linkField () {
      return this.datasets[this.idx].schema.find(f => f['x-refersTo'] === 'https://schema.org/WebPage')
    },
    queryParamsExtra () {
      if (!this.data) return {}
      return { [`_c_${this.layer.embed.concept['x-concept'].id}_eq`]: this.data[this.layer.embed.concept.key], primary: this.$vuetify.theme.themes.light.primary }
    },
    tableHeight () {
      if (!this.layer.embed.element.fillHeight || this.windowWidth < (this.config.largePanel ? 800 : 600) || this.legendIdx > 0) return 300
      else return this.windowHeight - 102 - (this.config.hideSearchBar ? 0 : 124) - this.layer.details.length * 42 - (this.layer.linkTemplate && this.urlTemplate ? 40 : 0) - (this.layer.embed.element.display !== 'list' ? 24 : 0)
    }
  },
  watch: {
    feature () {
      this.refresh()
    }
  },
  mounted () {
    this.urlTemplate = this.layer.linkTemplate && parseTemplate(this.layer.linkTemplate)
    this.refresh()
  },
  methods: {
    async refresh () {
      if (!this.layer) this.data = null
      else {
        const params = {
          qs: `_id:"${this.feature.id}"`,
          select: this.layer.details.join(',')
        }
        if (this.layer.gpxDownload) params.select += ',_geoshape'
        if (this.layer.labelField) params.select += ',' + this.layer.labelField
        if (this.layer.embed && this.layer.embed.type === 'embed') params.select += ',' + this.layer.embed.concept.key
        if (this.layer.linkTemplate) {
          const linkFields = this.layer.linkTemplate.match(/\{(.*?)\}/g).map(f => f.substring(1, f.length - 1))
          if (linkFields.length) params.select += ',' + linkFields.join(',')
        }
        this.data = (await axios.get(this.datasetInfos[this.layer.dataset.id].href + '/lines', { params })).data.results[0]
      }
    },
    formatField,
    downloadGPX (data) {
      const geojson = {
        type: 'Feature',
        properties: {},
        geometry: {
          type: data['_geoshape.type'],
          coordinates: data['_geoshape.coordinates']
        }
      }
      if (this.layer.labelField) geojson.properties.name = data[this.layer.labelField]
      const gpx = GeoJsonToGpx(geojson, {})
      const gpxString = new XMLSerializer().serializeToString(gpx)
      const link = document.createElement('a')
      link.download = (this.layer.labelField ? data[this.layer.labelField] : 'tracé') + '.gpx'
      const blob = new Blob([gpxString], { type: 'text/xml' })
      link.href = window.URL.createObjectURL(blob)
      link.click()
    }
  }
}
</script>
